import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Types
export interface CandidateNote {
  author: string;
  content: string;
  timestamp: string;
}

export interface SurveyCategory {
  name: string;
  score: number;
}

export interface SurveyResults {
  completed: boolean;
  score: number;
  categories: SurveyCategory[];
}

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  action: string;
  details: string;
  user?: string;
}

export interface Candidate {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  status: 'new' | 'culture_survey_sent' | 'culture_survey_complete' | 'interviewing' | 'offer_sent' | 'hired' | 'rejected';
  surveyStatus: 'not_sent' | 'sent' | 'completed' | 'expired';
  appliedDate: string;
  source?: string;
  resume?: string;
  coverLetter?: string;
  notes?: CandidateNote[];
  surveyResults?: SurveyResults;
  activityLog?: ActivityLogEntry[];
  updatedAt?: string;
}

// Mock data for development
const mockCandidates: Candidate[] = [
  {
    id: '1',
    name: 'John Smith',
    email: 'john.smith@email.com',
    phone: '+44 7700 900123',
    role: 'warehouse',
    status: 'new',
    surveyStatus: 'not_sent',
    appliedDate: '2024-12-18',
    source: 'Indeed',
    resume: '#',
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    phone: '+44 7700 900456',
    role: 'production',
    status: 'culture_survey_sent',
    surveyStatus: 'sent',
    appliedDate: '2024-12-17',
    source: 'LinkedIn',
    resume: '#',
    coverLetter: '#',
  },
  {
    id: '3',
    name: 'Michael Brown',
    email: 'mbrown@email.com',
    phone: '+44 7700 900789',
    role: 'driver',
    status: 'culture_survey_complete',
    surveyStatus: 'completed',
    appliedDate: '2024-12-15',
    source: 'Referral',
    resume: '#',
    surveyResults: {
      completed: true,
      score: 88,
      categories: [
        { name: 'Teamwork', score: 92 },
        { name: 'Communication', score: 85 },
        { name: 'Problem Solving', score: 88 },
        { name: 'Adaptability', score: 87 },
      ],
    },
    notes: [
      { author: 'HR Team', content: 'Strong candidate with good driving experience', timestamp: '2024-12-16T10:00:00Z' },
    ],
  },
  {
    id: '4',
    name: 'Emily Davis',
    email: 'emily.davis@email.com',
    phone: '+44 7700 900111',
    role: 'admin',
    status: 'interviewing',
    surveyStatus: 'completed',
    appliedDate: '2024-12-10',
    source: 'Company Website',
    resume: '#',
    surveyResults: {
      completed: true,
      score: 92,
      categories: [
        { name: 'Teamwork', score: 95 },
        { name: 'Communication', score: 94 },
        { name: 'Problem Solving', score: 88 },
        { name: 'Adaptability', score: 91 },
      ],
    },
  },
  {
    id: '5',
    name: 'James Wilson',
    email: 'jwilson@email.com',
    role: 'warehouse',
    status: 'offer_sent',
    surveyStatus: 'completed',
    appliedDate: '2024-12-05',
    source: 'Indeed',
    resume: '#',
    surveyResults: {
      completed: true,
      score: 85,
      categories: [
        { name: 'Teamwork', score: 88 },
        { name: 'Communication', score: 82 },
        { name: 'Problem Solving', score: 85 },
        { name: 'Adaptability', score: 85 },
      ],
    },
  },
  {
    id: '6',
    name: 'Lisa Anderson',
    email: 'l.anderson@email.com',
    phone: '+44 7700 900222',
    role: 'production',
    status: 'hired',
    surveyStatus: 'completed',
    appliedDate: '2024-11-25',
    source: 'Referral',
    resume: '#',
  },
  {
    id: '7',
    name: 'David Taylor',
    email: 'dtaylor@email.com',
    role: 'driver',
    status: 'rejected',
    surveyStatus: 'completed',
    appliedDate: '2024-12-01',
    source: 'LinkedIn',
    resume: '#',
    surveyResults: {
      completed: true,
      score: 52,
      categories: [
        { name: 'Teamwork', score: 55 },
        { name: 'Communication', score: 48 },
        { name: 'Problem Solving', score: 50 },
        { name: 'Adaptability', score: 55 },
      ],
    },
  },
];

// API functions
async function fetchCandidates(): Promise<Candidate[]> {
  // In production, this would be an actual API call
  // const response = await fetch('/api/candidates');
  // return response.json();

  await new Promise((resolve) => setTimeout(resolve, 500));
  return mockCandidates;
}

async function fetchCandidate(id: string): Promise<Candidate | null> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return mockCandidates.find((candidate) => candidate.id === id) || null;
}

async function updateCandidateStatus(data: { id: string; status: string; reason?: string }): Promise<Candidate> {
  await new Promise((resolve) => setTimeout(resolve, 500));
  const candidate = mockCandidates.find((c) => c.id === data.id);
  if (!candidate) throw new Error('Candidate not found');

  return {
    ...candidate,
    status: data.status as Candidate['status'],
    updatedAt: new Date().toISOString(),
  };
}

async function sendCultureSurvey(id: string): Promise<Candidate> {
  await new Promise((resolve) => setTimeout(resolve, 500));
  const candidate = mockCandidates.find((c) => c.id === id);
  if (!candidate) throw new Error('Candidate not found');

  return {
    ...candidate,
    status: 'culture_survey_sent',
    surveyStatus: 'sent',
    updatedAt: new Date().toISOString(),
  };
}

// React Query hooks
export function useCandidates() {
  return useQuery({
    queryKey: ['candidates'],
    queryFn: fetchCandidates,
  });
}

export function useCandidate(id: string) {
  return useQuery({
    queryKey: ['candidates', id],
    queryFn: () => fetchCandidate(id),
    enabled: !!id,
  });
}

export function useUpdateCandidateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCandidateStatus,
    onSuccess: (updatedCandidate) => {
      // Update the candidates list cache
      queryClient.setQueryData(['candidates'], (oldData: Candidate[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((candidate) =>
          candidate.id === updatedCandidate.id ? updatedCandidate : candidate
        );
      });

      // Update the individual candidate cache
      queryClient.setQueryData(['candidates', updatedCandidate.id], updatedCandidate);
    },
  });
}

export function useSendCultureSurvey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendCultureSurvey,
    onSuccess: (updatedCandidate) => {
      // Update the candidates list cache
      queryClient.setQueryData(['candidates'], (oldData: Candidate[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((candidate) =>
          candidate.id === updatedCandidate.id ? updatedCandidate : candidate
        );
      });

      // Update the individual candidate cache
      queryClient.setQueryData(['candidates', updatedCandidate.id], updatedCandidate);
    },
  });
}
